import dayjs from 'dayjs';

// Copied from components/HighCharts/utils.js so the renderer stays a pure,
// self-contained module. The published renderer keeps its own copy until the
// contract slice (ticket 08) removes the now-dead originals.
const formatDateValue = (value, formattingType) => {
    switch (formattingType) {
        case 'datetime':
            return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
        case 'date':
            return dayjs(value).format('YYYY-MM-DD');
        case 'year':
            return dayjs(value).format('YYYY');
        case 'monthday':
            return dayjs(value).format('DD/MM');
        case 'monthyear':
            return dayjs(value).format('MM/YYYY');
        case 'human-readable':
            return dayjs(value).format('MMM D, YYYY');
        default:
            return value;
    }
};

// Fenix rows carry dataset-specific field names; map them to the timeline's
// generic point shape using the serie's property mapping.
const toTimelinePoints = (items, serie = {}) => {
    const dateField = serie.dateProperty || 'date';
    const titleField = serie.titleProperty || 'title';
    const descField = serie.descriptionProperty || 'description';
    return items.map((item) => ({
        x: item[dateField],
        name: item[titleField],
        description: item[descField],
    }));
};

const sortPointsByDate = (points, ordering) =>
    [...points].sort((a, b) => {
        const at = dayjs(a.x).valueOf();
        const bt = dayjs(b.x).valueOf();
        if (ordering === 'asc') {
            return at - bt;
        } else if (ordering === 'desc') {
            return bt - at;
        }
    });

// Ensure each consecutive date has at least the specified month distance and
// enforce a maximum distance, so tightly-clustered events stay readable.
const enforceTimelineMinMonthDistance = (
    data,
    minMonths,
    maxMonths,
    ordering
) => {
    if (
        !Array.isArray(data) ||
        data.length === 0 ||
        !minMonths ||
        minMonths <= 0
    ) {
        return data;
    }

    const result = [];
    let prev = dayjs(data[0].x).startOf('month');

    result.push({
        ...data[0],
        spacedX: prev.format('YYYY-MM-DD'),
        x: data[0].x,
    });

    for (let i = 1; i < data.length; i++) {
        const orig = data[i].x;
        let cur = dayjs(data[i].x).startOf('month');

        const prevOriginal = dayjs(data[i - 1].x).startOf('month');
        const actualMonthDiff = Math.abs(cur.diff(prevOriginal, 'month'));

        const effectiveMinMonths = actualMonthDiff <= 1 ? 2 : minMonths;
        const effectiveMaxMonths = actualMonthDiff <= 1 ? 2 : maxMonths;

        if (ordering === 'desc') {
            const minThreshold = prev.subtract(effectiveMinMonths, 'month');
            const maxThreshold = prev.subtract(effectiveMaxMonths, 'month');

            if (cur.isAfter(minThreshold)) {
                cur = minThreshold;
            } else if (cur.isBefore(maxThreshold)) {
                cur = maxThreshold;
            }
        } else {
            const minThreshold = prev.add(effectiveMinMonths, 'month');
            const maxThreshold = prev.add(effectiveMaxMonths, 'month');

            if (cur.isBefore(minThreshold)) {
                cur = minThreshold;
            } else if (cur.isAfter(maxThreshold)) {
                cur = maxThreshold;
            }
        }

        prev = cur;
        result.push({
            ...data[i],
            spacedX: cur.format('YYYY-MM-DD'),
            x: orig,
        });
    }

    return result;
};

const buildTimelineSeriesData = (points, chartBlock) => {
    const ordering = chartBlock.dateOrdering;
    const sorted = enforceTimelineMinMonthDistance(
        sortPointsByDate(points, ordering),
        3,
        9,
        ordering || 'asc'
    );
    return sorted.map((point) => ({
        date: formatDateValue(point.x, chartBlock.dateFormattting),
        spaceDate: formatDateValue(point.spacedX, chartBlock.dateFormattting),
        name: point.name,
        description: point.description,
        ...(chartBlock.spacePointsEvenly
            ? {}
            : {
                  x: new Date(
                      point.spacedX ? point.spacedX : point.x
                  ).getTime(),
              }),
    }));
};

const timelineHeight = (chartBlock, seriesMaxItems) => {
    let rowHeight;
    let minHeight;
    if (!chartBlock.isDesktop) {
        rowHeight = chartBlock.spacePointsEvenly ? 104 : 158;
        minHeight = 404;
    } else {
        rowHeight = chartBlock.spacePointsEvenly ? 70 : 108;
        minHeight = 200;
    }
    return Math.max(seriesMaxItems * rowHeight, minHeight);
};

export const buildTimelineChartOptions = (chartBlock, rows) => {
    const queries = chartBlock.propertySeriesQueries || [];
    const series = (rows || []).map((items, index) => ({
        data: buildTimelineSeriesData(
            toTimelinePoints(items || [], queries[index]?.serie),
            chartBlock
        ),
    }));

    const seriesMaxItems = series.length
        ? Math.max(...series.map((s) => s.data.length))
        : 0;
    const height = timelineHeight(chartBlock, seriesMaxItems);

    return {
        title: { text: null },
        // Export-menu click handlers write files (a DOM concern) and merge
        // caller-side; the renderer emits only the menu structure.
        exporting: {
            enabled: true,
            menuItemDefinitions: {
                downloadCSV: { text: 'Download CSV' },
                downloadXLSX: { text: 'Download XLSX' },
            },
            buttons: {
                contextButton: {
                    menuItems: [
                        'downloadCSV',
                        'downloadXLSX',
                        'separator',
                        'viewFullscreen',
                        'printChart',
                    ],
                },
            },
        },
        chart: {
            // The DOM-measured marginLeft and the load handler are side effects
            // that the caller merges; they can't live in the pure renderer.
            zooming: { enabled: false },
            type: 'timeline',
            inverted: true,
            styledMode: false,
            backgroundColor: '#ffffff',
            height,
            spacingRight: 30,
        },
        xAxis: {
            type: 'datetime',
            visible: false,
            reversed: chartBlock.dateOrdering === 'desc' ? false : true,
        },
        yAxis: {
            gridLineWidth: 1,
            title: null,
            labels: { enabled: false },
        },
        legend: { enabled: false },
        // tooltip.formatter / tooltip.positioner read from the DOM and merge
        // caller-side.
        tooltip: {
            enabled: true,
            useHTML: true,
            hideDelay: 0,
            className: 'timeline-tooltip-wrapper',
        },
        series: [
            {
                enableMouseTracking: false,
                states: { hover: { enabled: false } },
                dataLabels: {
                    allowOverlap: true,
                    alternate: false,
                    distance: 10,
                    useHTML: true,
                    states: { hover: { enabled: false } },
                    format: `
                        <div class="timeline-card" style="width: var(--timeline-${chartBlock.chartId}-card-width, auto);">
                            <div class="timeline-date">
                                <span class="month-day">{point.date:%b %d, %Y}</span>
                            </div>

                            <div class="timeline-content">
                                <h3 class="timeline-title">{point.name}</h3>
                            </div>

                            <div class="timeline-info">
                                <button
                                    class="timeline-info-button timeline-point-index--{point.index}"
                                >
                                <span class="timeline-info-icon"></span>
                                </button>
                            </div>
                        </div>
                    `,
                },
                marker: { symbol: 'circle' },
                data: (series[0] && series[0].data) || [],
            },
        ],
    };
};
